import React, { useCallback, useState, useRef, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Download, Copy, CheckIcon, Home, Palette, Type, Layout, BookOpen, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
// ── Logo SVG Components ──
function LogoFull({
  color = '#111827',
  size = 200
}: {
  color?: string;
  size?: number;
}) {
  const scale = size / 200;
  return <svg width={size} height={size * 0.35} viewBox="0 0 200 70" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-184">
      <rect x="4" y="12" width="46" height="46" rx="10" fill="#2563EB" data-id="element-185" />
      <path d="M16 35L24 27L32 35L40 27" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-186" />
      <path d="M20 43H34" stroke="white" strokeWidth="3.5" strokeLinecap="round" data-id="element-187" />
      <text x="62" y="46" fontFamily="Inter, sans-serif" fontWeight="800" fontSize="32" fill={color} letterSpacing="-1" data-id="element-188">
        MINGA
      </text>
    </svg>;
}
function LogoIcon({
  color = '#2563EB',
  size = 80
}: {
  color?: string;
  size?: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-189">
      <rect x="8" y="8" width="64" height="64" rx="14" fill={color} data-id="element-190" />
      <path d="M24 40L32 32L40 40L48 32" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-191" />
      <path d="M28 52H52" stroke="white" strokeWidth="4" strokeLinecap="round" data-id="element-192" />
    </svg>;
}
// ── Utility: SVG to downloadable PNG ──
function useSvgDownload() {
  return useCallback((svgElement: SVGSVGElement | null, filename: string, scale: number = 4) => {
    if (!svgElement) return;
    const svgData = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([svgData], {
      type: 'image/svg+xml;charset=utf-8'
    });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0);
      canvas.toBlob(blob => {
        if (!blob) return;
        const pngUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = pngUrl;
        a.download = `${filename}.png`;
        a.click();
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, []);
}
// ── Copy to clipboard hook ──
function useCopyToClipboard() {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  }, []);
  return {
    copied,
    copy
  };
}
// ── Color Swatch ──
function ColorSwatch({
  name,
  hex,
  textColor = 'text-white'
}: {
  name: string;
  hex: string;
  textColor?: string;
}) {
  const {
    copied,
    copy
  } = useCopyToClipboard();
  const isCopied = copied === hex;
  return <button onClick={() => copy(hex)} className="group relative rounded-2xl overflow-hidden border border-gray-200 transition-all hover:shadow-md hover:scale-[1.02] text-left" data-id="element-193">
      <div className={`h-28 flex items-end p-4 ${textColor}`} style={{
      backgroundColor: hex
    }} data-id="element-194">
        <span className="text-sm font-bold opacity-90" data-id="element-195">{name}</span>
      </div>
      <div className="bg-white px-4 py-3 flex items-center justify-between" data-id="element-196">
        <code className="text-sm font-mono text-gray-700" data-id="element-197">{hex}</code>
        {isCopied ? <CheckIcon size={14} className="text-green-500" data-id="element-198" /> : <Copy size={14} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-199" />}
      </div>
    </button>;
}
// ── Logo Download Card ──
function LogoCard({
  title,
  description,
  children,
  onDownload,
  bgClass = 'bg-white'
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  onDownload: () => void;
  bgClass?: string;
}) {
  return <div className="rounded-2xl border border-gray-200 overflow-hidden" data-id="element-200">
      <div className={`${bgClass} p-10 flex items-center justify-center min-h-[180px]`} data-id="element-201">
        {children}
      </div>
      <div className="bg-white px-6 py-4 border-t border-gray-100 flex items-center justify-between" data-id="element-202">
        <div data-id="element-203">
          <p className="text-sm font-semibold text-gray-900" data-id="element-204">{title}</p>
          <p className="text-xs text-gray-500" data-id="element-205">{description}</p>
        </div>
        <button onClick={onDownload} className="flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg transition-colors" data-id="element-206">
          <Download size={14} data-id="element-207" />
          PNG
        </button>
      </div>
    </div>;
}
// ── Section wrapper ──
function Section({
  id,
  title,
  icon: Icon,
  children
}: {
  id: string;
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return <motion.section id={id} initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="scroll-mt-24" data-id="element-208">
      <div className="flex items-center gap-3 mb-8" data-id="element-209">
        <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600" data-id="element-210">
          <Icon size={20} data-id="element-211" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-212">{title}</h2>
      </div>
      {children}
    </motion.section>;
}
// ── Main Page ──
export function BrandGuidelines() {
  const downloadSvg = useSvgDownload();
  const logoFullDarkRef = useRef<SVGSVGElement>(null);
  const logoFullWhiteRef = useRef<SVGSVGElement>(null);
  const logoIconRef = useRef<SVGSVGElement>(null);
  const logoIconWhiteRef = useRef<SVGSVGElement>(null);
  const navItems = [{
    id: 'logo',
    label: 'Logo',
    icon: Home
  }, {
    id: 'colores',
    label: 'Colores',
    icon: Palette
  }, {
    id: 'tipografia',
    label: 'Tipografía',
    icon: Type
  }, {
    id: 'uso',
    label: 'Uso y reglas',
    icon: Layout
  }, {
    id: 'tono',
    label: 'Tono de voz',
    icon: BookOpen
  }];
  return <div className="min-h-screen bg-gray-50 font-sans" data-id="element-213">
      {/* Top bar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100" data-id="element-214">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between" data-id="element-215">
          <div className="flex items-center gap-4" data-id="element-216">
            <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors text-sm" data-id="element-217">
              <ArrowLeft size={16} data-id="element-218" />
              Volver al sitio
            </Link>
            <div className="w-px h-5 bg-gray-200" data-id="element-219" />
            <div className="flex items-center gap-2" data-id="element-220">
              <svg width="24" height="24" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-221">
                <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-222" />
                <path d="M16 38L28 24L40 38L52 24" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-223" />
                <path d="M22 48H42" stroke="white" strokeWidth="4.5" strokeLinecap="round" data-id="element-224" />
              </svg>
              <span className="font-bold text-sm text-gray-900 tracking-tight" data-id="element-225">
                MINGA — Brand Guidelines
              </span>
            </div>
          </div>
          <span className="text-xs text-gray-400 font-medium" data-id="element-226">Uso interno</span>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pt-24 pb-20" data-id="element-227">
        {/* Hero */}
        <div className="mb-16" data-id="element-228">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 mb-4" data-id="element-229">
            Guía de marca
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl" data-id="element-230">
            Todo lo que necesitas para usar la identidad visual de MINGA
            correctamente. Logos, colores, tipografía y reglas de uso.
          </p>
        </div>

        {/* Quick nav */}
        <div className="flex flex-wrap gap-2 mb-16" data-id="element-231">
          {navItems.map(item => <a key={item.id} href={`#${item.id}`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all" data-id="element-232">
              <item.icon size={14} data-id="element-233" />
              {item.label}
            </a>)}
        </div>

        <div className="space-y-24" data-id="element-234">
          {/* ── LOGO ── */}
          <Section id="logo" title="Logo" icon={Home} data-id="element-235">
            <p className="text-gray-600 mb-8 max-w-2xl" data-id="element-236">
              El logo de MINGA tiene dos versiones: completo (isotipo + texto) y
              solo isotipo. Descarga en PNG con fondo transparente a máxima
              resolución (4x).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-237">
              <LogoCard title="Logo completo — Oscuro" description="Fondo claro · PNG transparente" onDownload={() => downloadSvg(logoFullDarkRef.current, 'minga-logo-dark')} data-id="element-238">
                <svg ref={logoFullDarkRef} width="200" height="70" viewBox="0 0 200 70" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-239">
                  <rect x="4" y="12" width="46" height="46" rx="10" fill="#2563EB" data-id="element-240" />
                  <path d="M16 35L24 27L32 35L40 27" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-241" />
                  <path d="M20 43H34" stroke="white" strokeWidth="3.5" strokeLinecap="round" data-id="element-242" />
                  <text x="62" y="46" fontFamily="Inter, sans-serif" fontWeight="800" fontSize="32" fill="#111827" letterSpacing="-1" data-id="element-243">
                    MINGA
                  </text>
                </svg>
              </LogoCard>

              <LogoCard title="Logo completo — Blanco" description="Fondo oscuro · PNG transparente" bgClass="bg-gray-900" onDownload={() => downloadSvg(logoFullWhiteRef.current, 'minga-logo-white')} data-id="element-244">
                <svg ref={logoFullWhiteRef} width="200" height="70" viewBox="0 0 200 70" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-245">
                  <rect x="4" y="12" width="46" height="46" rx="10" fill="#2563EB" data-id="element-246" />
                  <path d="M16 35L24 27L32 35L40 27" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-247" />
                  <path d="M20 43H34" stroke="white" strokeWidth="3.5" strokeLinecap="round" data-id="element-248" />
                  <text x="62" y="46" fontFamily="Inter, sans-serif" fontWeight="800" fontSize="32" fill="white" letterSpacing="-1" data-id="element-249">
                    MINGA
                  </text>
                </svg>
              </LogoCard>

              <LogoCard title="Isotipo — Color" description="Fondo claro · PNG transparente" onDownload={() => downloadSvg(logoIconRef.current, 'minga-icon-color')} data-id="element-250">
                <svg ref={logoIconRef} width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-251">
                  <rect x="8" y="8" width="64" height="64" rx="14" fill="#2563EB" data-id="element-252" />
                  <path d="M24 40L32 32L40 40L48 32" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-253" />
                  <path d="M28 52H52" stroke="white" strokeWidth="4" strokeLinecap="round" data-id="element-254" />
                </svg>
              </LogoCard>

              <LogoCard title="Isotipo — Blanco" description="Fondo oscuro · PNG transparente" bgClass="bg-gray-900" onDownload={() => downloadSvg(logoIconWhiteRef.current, 'minga-icon-white')} data-id="element-255">
                <svg ref={logoIconWhiteRef} width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-256">
                  <rect x="8" y="8" width="64" height="64" rx="14" fill="white" data-id="element-257" />
                  <path d="M24 40L32 32L40 40L48 32" stroke="#2563EB" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" data-id="element-258" />
                  <path d="M28 52H52" stroke="#2563EB" strokeWidth="4" strokeLinecap="round" data-id="element-259" />
                </svg>
              </LogoCard>
            </div>
          </Section>

          {/* ── EXPLORACIÓN ISOTIPOS ── */}
          <Section id="isotipos" title="Exploración de isotipos" icon={Palette} data-id="element-260">
            <p className="text-gray-600 mb-4 max-w-2xl" data-id="element-261">
              10 variaciones del bloque azul. El texto MINGA no cambia — solo
              exploramos el ícono interior.
            </p>
            <p className="text-sm text-gray-400 mb-10" data-id="element-262">
              Elige tu favorito y cuéntanos cuál representa mejor a MINGA.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-6" data-id="element-263">
              {/* 1 — Casita simple */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-264">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-265">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-266">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-267" />
                    <path d="M32 16L14 30H20V46H28V38H36V46H44V30H50L32 16Z" fill="white" data-id="element-268" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-269">
                  Casita
                </span>
              </div>

              {/* 2 — Llave */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-270">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-271">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-272">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-273" />
                    <circle cx="26" cy="28" r="8" stroke="white" strokeWidth="3" fill="none" data-id="element-274" />
                    <path d="M32 34L48 34" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-275" />
                    <path d="M44 34V42" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-276" />
                    <path d="M48 34V40" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-277" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-278">Llave</span>
              </div>

              {/* 3 — Puerta */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-279">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-280">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-281">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-282" />
                    <rect x="20" y="14" width="24" height="36" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-283" />
                    <circle cx="38" cy="32" r="2" fill="white" data-id="element-284" />
                    <path d="M20 50H44" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-285" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-286">
                  Puerta
                </span>
              </div>

              {/* 4 — Techo / peak */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-287">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-288">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-289">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-290" />
                    <path d="M12 36L32 18L52 36" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-291" />
                    <path d="M20 44H44" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-292" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-293">Techo</span>
              </div>

              {/* 5 — Ventana grid */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-294">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-295">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-296">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-297" />
                    <rect x="16" y="16" width="14" height="14" rx="3" fill="white" data-id="element-298" />
                    <rect x="34" y="16" width="14" height="14" rx="3" fill="white" data-id="element-299" />
                    <rect x="16" y="34" width="14" height="14" rx="3" fill="white" data-id="element-300" />
                    <rect x="34" y="34" width="14" height="14" rx="3" fill="white" opacity="0.4" data-id="element-301" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-302">
                  Ventanas
                </span>
              </div>

              {/* 6 — Planta / crecimiento */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-303">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-304">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-305">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-306" />
                    <path d="M32 48V28" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-307" />
                    <path d="M32 34C32 34 22 32 22 24C22 16 32 18 32 18" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-308" />
                    <path d="M32 28C32 28 42 26 42 18C42 12 32 14 32 14" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none" data-id="element-309" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-310">
                  Crecimiento
                </span>
              </div>

              {/* 7 — Apretón / deal */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-311">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-312">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-313">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-314" />
                    <path d="M16 34L24 26L32 30L40 24L48 30" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-315" />
                    <path d="M24 42H40" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-316" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-317">
                  Conexión
                </span>
              </div>

              {/* 8 — Pin / ubicación */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-318">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-319">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-320">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-321" />
                    <path d="M32 48L32 48C32 48 44 36 44 26C44 19.4 38.6 14 32 14C25.4 14 20 19.4 20 26C20 36 32 48 32 48Z" stroke="white" strokeWidth="3" fill="none" data-id="element-322" />
                    <circle cx="32" cy="26" r="5" fill="white" data-id="element-323" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-324">
                  Ubicación
                </span>
              </div>

              {/* 9 — Corazón + techo */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-325">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-326">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-327">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-328" />
                    <path d="M32 46C32 46 16 36 16 26C16 20 20 16 26 16C29 16 31 17.5 32 19C33 17.5 35 16 38 16C44 16 48 20 48 26C48 36 32 46 32 46Z" fill="white" data-id="element-329" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-330">Hogar</span>
              </div>

              {/* 10 — M abstracta */}
              <div className="flex flex-col items-center gap-3 group" data-id="element-331">
                <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-center justify-center transition-all group-hover:shadow-md group-hover:scale-105 group-hover:border-brand-200" data-id="element-332">
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" data-id="element-333">
                    <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-334" />
                    <path d="M16 44V20L28 36L32 30L36 36L48 20V44" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-335" />
                  </svg>
                </div>
                <span className="text-xs text-gray-500 font-medium" data-id="element-336">
                  M abstracta
                </span>
              </div>
            </div>

            {/* Preview with text */}
            <div className="mt-12" data-id="element-337">
              <p className="text-sm text-gray-400 font-medium mb-4" data-id="element-338">
                Vista previa con texto
              </p>
              <div className="flex flex-wrap gap-6" data-id="element-339">
                {[{
                label: 'Casita',
                icon: <>
                        <path d="M32 16L14 30H20V46H28V38H36V46H44V30H50L32 16Z" fill="white" data-id="element-340" />
                      </>
              }, {
                label: 'Techo',
                icon: <>
                        <path d="M12 36L32 18L52 36" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-341" />
                        <path d="M20 44H44" stroke="white" strokeWidth="3" strokeLinecap="round" data-id="element-342" />
                      </>
              }, {
                label: 'M abstracta',
                icon: <>
                        <path d="M16 44V20L28 36L32 30L36 36L48 20V44" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-343" />
                      </>
              }].map(v => <div key={v.label} className="bg-white rounded-2xl border border-gray-200 px-8 py-6 flex items-center gap-3" data-id="element-344">
                    <svg width="36" height="36" viewBox="0 0 64 64" fill="none" data-id="element-345">
                      <rect width="64" height="64" rx="14" fill="#2563EB" data-id="element-346" />
                      {v.icon}
                    </svg>
                    <span className="text-xl font-extrabold tracking-tight text-gray-900" data-id="element-347">
                      MINGA
                    </span>
                  </div>)}
              </div>
            </div>
          </Section>

          {/* ── COLORES ── */}
          <Section id="colores" title="Colores" icon={Palette} data-id="element-348">
            <p className="text-gray-600 mb-8 max-w-2xl" data-id="element-349">
              Paleta oficial de MINGA. Haz clic en cualquier color para copiar
              el código hex.
            </p>

            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-350">
              Primarios
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-id="element-351">
              <ColorSwatch name="Brand 600" hex="#2563EB" data-id="element-352" />
              <ColorSwatch name="Brand 700" hex="#1D4ED8" data-id="element-353" />
              <ColorSwatch name="Brand 500" hex="#3B82F6" data-id="element-354" />
              <ColorSwatch name="Brand 900" hex="#1E3A8A" data-id="element-355" />
            </div>

            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-356">
              Secundarios
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-id="element-357">
              <ColorSwatch name="Brand 50" hex="#EFF6FF" textColor="text-brand-700" data-id="element-358" />
              <ColorSwatch name="Brand 100" hex="#DBEAFE" textColor="text-brand-700" data-id="element-359" />
            </div>

            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-360">
              Neutros
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-id="element-361">
              <ColorSwatch name="Negro" hex="#111827" data-id="element-362" />
              <ColorSwatch name="Gris 700" hex="#374151" data-id="element-363" />
              <ColorSwatch name="Gris 500" hex="#6B7280" data-id="element-364" />
              <ColorSwatch name="Gris 100" hex="#F3F4F6" textColor="text-gray-700" data-id="element-365" />
              <ColorSwatch name="Blanco" hex="#FFFFFF" textColor="text-gray-700" data-id="element-366" />
            </div>
          </Section>

          {/* ── TIPOGRAFÍA ── */}
          <Section id="tipografia" title="Tipografía" icon={Type} data-id="element-367">
            <p className="text-gray-600 mb-8 max-w-2xl" data-id="element-368">
              Usamos <strong data-id="element-369">Inter</strong> como tipografía única. Se importa
              desde Google Fonts. Estos son los pesos y tamaños que usamos.
            </p>

            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden divide-y divide-gray-100" data-id="element-370">
              {[{
              label: 'Headline XL',
              weight: 'font-extrabold',
              size: 'text-5xl',
              tracking: 'tracking-tight',
              sample: 'Vende tu propiedad'
            }, {
              label: 'Headline L',
              weight: 'font-bold',
              size: 'text-4xl',
              tracking: 'tracking-tight',
              sample: 'Un sistema simple'
            }, {
              label: 'Headline M',
              weight: 'font-bold',
              size: 'text-2xl',
              tracking: 'tracking-tight',
              sample: 'Planes transparentes'
            }, {
              label: 'Body L',
              weight: 'font-medium',
              size: 'text-xl',
              tracking: '',
              sample: 'Publicamos mejor, filtramos compradores reales'
            }, {
              label: 'Body',
              weight: 'font-normal',
              size: 'text-base',
              tracking: '',
              sample: 'Para quienes quieren vender solos, pero sin cometer errores básicos'
            }, {
              label: 'Small',
              weight: 'font-medium',
              size: 'text-sm',
              tracking: '',
              sample: 'Revisión y optimización de tu publicación'
            }, {
              label: 'Caption',
              weight: 'font-normal',
              size: 'text-xs',
              tracking: '',
              sample: 'Nosotros no actuamos como corredores ni intermediamos la venta'
            }].map(item => <div key={item.label} className="flex flex-col md:flex-row md:items-center gap-4 p-6" data-id="element-371">
                  <div className="w-32 shrink-0" data-id="element-372">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider" data-id="element-373">
                      {item.label}
                    </p>
                    <p className="text-xs text-gray-400 mt-1" data-id="element-374">
                      {item.weight} · {item.size}
                    </p>
                  </div>
                  <p className={`${item.size} ${item.weight} ${item.tracking} text-gray-900 flex-1`} data-id="element-375">
                    {item.sample}
                  </p>
                </div>)}
            </div>

            <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-6" data-id="element-376">
              <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-377">
                Importar Inter
              </h3>
              <code className="block bg-gray-50 rounded-xl p-4 text-sm text-gray-700 font-mono overflow-x-auto" data-id="element-378">
                @import
                url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
              </code>
            </div>
          </Section>

          {/* ── USO Y REGLAS ── */}
          <Section id="uso" title="Uso y reglas" icon={Layout} data-id="element-379">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-380">
              <div className="bg-white rounded-2xl border border-gray-200 p-8" data-id="element-381">
                <h3 className="text-lg font-bold text-green-700 mb-4" data-id="element-382">✅ Sí</h3>
                <ul className="space-y-3 text-sm text-gray-700" data-id="element-383">
                  <li data-id="element-384">Usar el logo sobre fondos blancos o muy claros</li>
                  <li data-id="element-385">Usar la versión blanca sobre fondos oscuros</li>
                  <li data-id="element-386">
                    Mantener el espacio mínimo alrededor del logo (1x la altura
                    del isotipo)
                  </li>
                  <li data-id="element-387">Usar los colores exactos de la paleta</li>
                  <li data-id="element-388">Usar Inter como única tipografía</li>
                  <li data-id="element-389">Mantener el tono directo y cercano en textos</li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 p-8" data-id="element-390">
                <h3 className="text-lg font-bold text-red-600 mb-4" data-id="element-391">❌ No</h3>
                <ul className="space-y-3 text-sm text-gray-700" data-id="element-392">
                  <li data-id="element-393">Cambiar los colores del logo</li>
                  <li data-id="element-394">Distorsionar, rotar o recortar el logo</li>
                  <li data-id="element-395">Usar el logo sobre fondos con poco contraste</li>
                  <li data-id="element-396">Agregar sombras, bordes o efectos al logo</li>
                  <li data-id="element-397">Usar tipografías diferentes a Inter</li>
                  <li data-id="element-398">Usar gradientes en elementos de marca</li>
                  <li data-id="element-399">Usar lenguaje corporativo o genérico</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-white rounded-2xl border border-gray-200 p-8" data-id="element-400">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-401">
                Tamaños mínimos
              </h3>
              <div className="flex flex-wrap gap-10 items-end" data-id="element-402">
                <div className="text-center" data-id="element-403">
                  <div className="bg-gray-50 rounded-xl p-6 mb-3 inline-flex" data-id="element-404">
                    <LogoIcon size={24} data-id="element-405" />
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-406">24px — Mínimo isotipo</p>
                </div>
                <div className="text-center" data-id="element-407">
                  <div className="bg-gray-50 rounded-xl p-6 mb-3 inline-flex" data-id="element-408">
                    <LogoIcon size={40} data-id="element-409" />
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-410">
                    40px — Recomendado app
                  </p>
                </div>
                <div className="text-center" data-id="element-411">
                  <div className="bg-gray-50 rounded-xl p-6 mb-3 inline-flex" data-id="element-412">
                    <LogoFull size={140} data-id="element-413" />
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-414">
                    140px — Mínimo logo completo
                  </p>
                </div>
              </div>
            </div>
          </Section>

          {/* ── TONO DE VOZ ── */}
          <Section id="tono" title="Tono de voz" icon={BookOpen} data-id="element-415">
            <div className="bg-white rounded-2xl border border-gray-200 p-8" data-id="element-416">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-417">
                <div data-id="element-418">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-419">
                    Somos
                  </h3>
                  <ul className="space-y-3" data-id="element-420">
                    {['Directos', 'Cercanos', 'Claros', 'Confiables', 'Prácticos'].map(item => <li key={item} className="flex items-center gap-3" data-id="element-421">
                        <div className="w-2 h-2 rounded-full bg-brand-600" data-id="element-422" />
                        <span className="text-gray-700 font-medium" data-id="element-423">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-424">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-425">
                    No somos
                  </h3>
                  <ul className="space-y-3" data-id="element-426">
                    {['Corporativos', 'Genéricos', 'Técnicos sin contexto', 'Agresivos', 'Condescendientes'].map(item => <li key={item} className="flex items-center gap-3" data-id="element-427">
                        <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-428" />
                        <span className="text-gray-500" data-id="element-429">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-gray-100" data-id="element-430">
                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4" data-id="element-431">
                  Ejemplos
                </h3>
                <div className="space-y-4" data-id="element-432">
                  <div className="flex flex-col md:flex-row gap-4" data-id="element-433">
                    <div className="flex-1 bg-red-50 rounded-xl p-4" data-id="element-434">
                      <p className="text-xs font-semibold text-red-500 mb-1" data-id="element-435">
                        ❌ Evitar
                      </p>
                      <p className="text-sm text-gray-700" data-id="element-436">
                        "Nuestra solución integral le permite optimizar el
                        proceso de comercialización de su inmueble"
                      </p>
                    </div>
                    <div className="flex-1 bg-green-50 rounded-xl p-4" data-id="element-437">
                      <p className="text-xs font-semibold text-green-600 mb-1" data-id="element-438">
                        ✅ Preferir
                      </p>
                      <p className="text-sm text-gray-700" data-id="element-439">
                        "Te ayudamos a vender tu propiedad sin perder tiempo con
                        compradores que no sirven"
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-4" data-id="element-440">
                    <div className="flex-1 bg-red-50 rounded-xl p-4" data-id="element-441">
                      <p className="text-xs font-semibold text-red-500 mb-1" data-id="element-442">
                        ❌ Evitar
                      </p>
                      <p className="text-sm text-gray-700" data-id="element-443">
                        "Contáctenos para agendar una reunión con nuestro equipo
                        de asesores"
                      </p>
                    </div>
                    <div className="flex-1 bg-green-50 rounded-xl p-4" data-id="element-444">
                      <p className="text-xs font-semibold text-green-600 mb-1" data-id="element-445">
                        ✅ Preferir
                      </p>
                      <p className="text-sm text-gray-700" data-id="element-446">
                        "Agenda un diagnóstico gratuito y te mostramos cómo
                        vender mejor"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>;
}